import { Tab } from "@/types/tab";
import "./index.scss";
import Exposure from "@/components/exposure";
import { useCallback, useMemo } from "react";
import { useTracker } from "@/context/TrackerContext";
import { TABBAR_BID_MC, TABBAR_BID_MV } from "@/track/waimai";

const TabbarItemSkeleton = () => (
  <div className="tabbar-item-skeleton skeleton-item">
    <div className="tabbar-item-icon"></div>
    <div className="tabbar-item-label"></div>
  </div>
);

const TabbarSkeleton = () => (
  <div className="tabbar-skeleton">
    <TabbarItemSkeleton />
    <TabbarItemSkeleton />
    <TabbarItemSkeleton />
    <TabbarItemSkeleton />
    <TabbarItemSkeleton />
    <TabbarItemSkeleton />
  </div>
);

interface TabbarItemProps {
  tab: Tab;
  index: number;
  isActive: boolean;
  onTabClick: (tab: Tab) => void;
}
const TabbarItem: React.FC<TabbarItemProps> = function (props) {
  const { tab, index, isActive, onTabClick } = props;
  const tracker = useTracker();

  const lxCustom = useMemo(() => ({
    tab_id: tab.name,
    tab_index: index,
  }), [tab, index]);

  const handleTabExpose = useCallback(() => {
    if (!tracker) return;
    tracker.moduleView(TABBAR_BID_MV.TAB, lxCustom);
  }, [tracker, lxCustom]);

  const handleTabClick = useCallback(() => {
    if (tracker) {
      tracker.moduleClick(TABBAR_BID_MC.TAB, lxCustom);
    }
    onTabClick(tab);
  }, [tracker, tab, lxCustom, onTabClick]);
  
  return (
    <Exposure onExpose={handleTabExpose}>
      <div className={`tabbar-item ${isActive ? "is-active" : ""}`} onClick={handleTabClick}>
        {tab.name}
      </div>
    </Exposure>
  );
};

interface TabbarProps {
  isInitFinished: boolean;
  tabList: Array<Tab>;
  activeTab: Tab | null;
  onTabClick: (tab: Tab) => void;
}

const Tabbar: React.FC<TabbarProps> = function (props) {
  const { isInitFinished, tabList, activeTab, onTabClick } = props;

  if (!isInitFinished) {
    return <TabbarSkeleton />;
  } else if (!Array.isArray(tabList) || tabList.length === 0) {
    return null;
  }

  return (
    <div className="tabbar">
      {tabList.map((tab, index) => (
        <TabbarItem
          key={index}
          index={index + 1}
          tab={tab}
          isActive={tab.id === (activeTab?.id || null)}
          onTabClick={onTabClick}
        />
      ))}
    </div>
  );
};

export default Tabbar;
